import { CalendarIcon, PlusIcon } from '@heroicons/react/24/outline'
import {
  Button,
  Card,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Switch
} from '@heroui/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useSnapshot } from 'valtio'
import { ErrorDisplay, logError } from '@/components/Error/Error'
import { Loading } from '@/components/Loading/Loading'
import type { Flag, Item, ItemRelease } from '../../../../types'
import { RichTextEditor } from '../../components/settings/RichTextEditor'
import { api } from '../../lib/api'
import { state, useRadarNav } from '../../lib/state'

// Helper to parse ISO date as local date
function parseLocalDate(isoDateStr: string) {
  const [year, month, day] = isoDateStr.split('-').map(Number)
  return new Date(year, month - 1, day)
}

// Wrapper component for date input to ensure consistent styling
const ReleaseDateInput: React.FC<
  React.ComponentProps<typeof Input>
> = props => {
  return (
    <div className="relative">
      <Input
        {...props}
        classNames={{
          ...props.classNames,
          input: `${props.classNames?.input || ''} caret-[var(--ds-text)]`
        }}
      />
      <CalendarIcon
        className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--ds-text-subtle)] pointer-events-none"
        aria-hidden="true"
      />
    </div>
  )
}

const AdminEditItemPage = () => {
  const snap = useSnapshot(state)
  const { radarId } = useRadarNav()
  const { itemId } = useParams()
  const navigate = useNavigate()
  const config = snap?.radarConfig ? { ...snap.radarConfig } : null
  const [item, setItem] = useState<Item | null>(null)

  // Track if we've initialized the version field
  const hasInitializedVersion = useRef(false)

  // Helper to update both local item and global radarItems
  const updateItemAndState = useCallback((updatedItem: Item | null) => {
    setItem(updatedItem)
    if (!updatedItem) return
    // Update the specific item in place (never reassign the proxy array)
    const idx = state.radarItems.findIndex(i => i.id === updatedItem.id)
    if (idx !== -1) {
      Object.assign(state.radarItems[idx], updatedItem)
    }
  }, [])

  const [selectedRelease, setSelectedRelease] = useState<ItemRelease | null>(
    null
  )
  const [isLoading, setIsLoading] = useState(true)
  const [localRelease, setLocalRelease] = useState<ItemRelease | null>(null)
  const [localVersion, setLocalVersion] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [_error, setError] = useState<string | null>(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deleteType, setDeleteType] = useState<'item' | 'release'>('item')
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isArchived, setIsArchived] = useState(false)
  const [pendingQuadrant, setPendingQuadrant] = useState<string | null>(null)
  const [pendingTags, setPendingTags] = useState<string[] | null>(null)

  // Add styles for dropdowns
  useEffect(() => {
    // Add the CSS for dropdowns to the document head
    const styleElement = document.createElement('style')
    styleElement.textContent = `
      .dropdown-content {
        --dropdown-bg: #ffffff;
        --dropdown-text: #172B4D;
        --dropdown-border: #DFE1E6;
        --dropdown-hover: #F4F5F7;
        --dropdown-selected: #EBECF0;
        --dropdown-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
      }
    
      @media (prefers-color-scheme: dark) {
        .dropdown-content {
          --dropdown-bg: #282E33;
          --dropdown-text: #FFFFFF;
          --dropdown-border: #505050;
          --dropdown-hover: #3E474F;
          --dropdown-selected: #353D45;
          --dropdown-shadow: 0 4px 8px rgba(0, 0, 0, 0.5);
        }

        /* Fix for delete confirmation modal text color in dark mode */
        .heroui-c-PJLV-ikzDmfj-css {
          background-color: #282E33 !important;
        }
        
        .heroui-modal-content {
          background-color: #282E33 !important;
          color: #E6EDFC !important;
        }
        
        .heroui-modal-header h3 {
          color: #E6EDFC !important;
        }
        
        .heroui-modal-body p {
          color: #E6EDFC !important;
        }
      }
    
      .dropdown-content {
        background-color: var(--dropdown-bg) !important;
        color: var(--dropdown-text) !important;
        border: 1px solid var(--dropdown-border) !important;
        box-shadow: var(--dropdown-shadow) !important;
      }
    
      .dropdown-item {
        background-color: var(--dropdown-bg) !important;
        color: var(--dropdown-text) !important;
        transition: background-color 0.2s ease;
      }
    
      .dropdown-item:hover {
        background-color: var(--dropdown-hover) !important;
      }
    
      .dropdown-item[data-selected=true] {
        background-color: var(--dropdown-selected) !important;
      }
    `
    document.head.appendChild(styleElement)

    // Cleanup function to remove the style element when component unmounts
    return () => {
      document.head.removeChild(styleElement)
    }
  }, []) // Empty dependency array means this runs once on mount

  // Load item data
  useEffect(() => {
    const loadItem = async () => {
      try {
        if (radarId == null) throw new Error('radarId is required')
        const data = await api.getRadarData(Number(radarId))
        const foundItem = data.items.find(i => i.id === itemId)
        if (foundItem) {
          updateItemAndState(foundItem)
          setIsArchived(foundItem.archived || false)
          // Select the most recent release by default
          if (foundItem.releases.length > 0) {
            // Create a copy of the readonly array to sort
            const releasesArray = [...foundItem.releases]
            const sortedReleases = releasesArray.sort(
              (a, b) =>
                parseLocalDate(b.releaseDate).getTime() -
                parseLocalDate(a.releaseDate).getTime()
            )
            const initialRelease = sortedReleases[0] || null
            setSelectedRelease(initialRelease)
            setLocalRelease(initialRelease)
          }
        } else {
          // Item not found, redirect to items list
          navigate('/admin/items')
          return
        }
        // Set localVersion from config.version if available
        if (config?.version) {
          setLocalVersion(config.version)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load item')
      } finally {
        setIsLoading(false)
      }
    }
    loadItem()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, radarId, config?.version, updateItemAndState, navigate])

  // Update local release when selected release changes
  // Only update localRelease when selectedRelease changes (not on every save or local edit)
  useEffect(() => {
    setLocalRelease(selectedRelease)
    setHasUnsavedChanges(false)
    // Only initialize localVersion from config.version on first mount
    if (!hasInitializedVersion.current && config?.version) {
      setLocalVersion(config.version)
      hasInitializedVersion.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRelease, config?.version])

  useEffect(() => {
    if (item) {
      setPendingQuadrant(item?.quadrant)
      setPendingTags(Array.from(item?.tags ?? []))
    }
  }, [item])

  const handleUpdateItemProperties = async (
    quadrant: string,
    tags: string[],
    archived: boolean = isArchived
  ) => {
    if (!item) return
    if (radarId == null) return
    try {
      setIsSubmitting(true)
      await api.updateItemProperties(
        item.id,
        quadrant,
        Array.from(tags),
        archived,
        Number(radarId)
      )
      updateItemAndState(
        item ? { ...item, quadrant, tags: Array.from(tags), archived } : null
      )
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update item properties'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleArchiveToggle = (archived: boolean) => {
    setIsArchived(archived)
    if (item) {
      handleUpdateItemProperties(item.quadrant, item.tags, archived)
    }
  }

  // Handler for local changes to release fields
  const handleLocalChange = (changes: Partial<ItemRelease>) => {
    setLocalRelease(prev => (prev ? { ...prev, ...changes } : null))
    setHasUnsavedChanges(true)
  }

  // Handler for version input changes
  const handleVersionChange = (value: string) => {
    setLocalVersion(value)
    setHasUnsavedChanges(true)
  }

  // Handler for updating both the release and item, and saving config version
  const handleUpdateReleaseAndItem = async () => {
    if (!item || !localRelease || !config || radarId == null) return
    try {
      setIsSubmitting(true)
      // Compose the updated release object
      const updatedRelease = { ...localRelease }
      // Compose the updated item object
      const updatedItem = {
        ...item,
        quadrant: pendingQuadrant ?? item.quadrant,
        tags: Array.from(pendingTags ?? item.tags),
        archived: isArchived,
        releases: item.releases.map(r =>
          r.id === localRelease.id ? updatedRelease : r
        )
      }
      // Call APIs to update both item, release, and config version
      await api.updateItemProperties(
        updatedItem.id,
        updatedItem.quadrant,
        updatedItem.tags,
        updatedItem.archived,
        Number(radarId)
      )
      await api.updateRelease(updatedItem.id, updatedRelease, Number(radarId))
      // Save version if changed
      // Always attempt to save version when updating release

      const safeConfig = {
        ...config,
        version: localVersion,
        sections: Array.isArray(config.sections) ? [...config.sections] : [],
        quadrants: Array.isArray(config.quadrants) ? [...config.quadrants] : [],
        rings: Array.isArray(config.rings) ? [...config.rings] : []
      }

      const setConfigResponse = await api.setConfig(safeConfig, Number(radarId))

      if (!setConfigResponse) {
        setError('Failed to save radar version.')
        setIsSubmitting(false)
        return
      }
      // Update global state config version
      if (state.radarConfig) {
        state.radarConfig = {
          ...state.radarConfig,
          version: localVersion,
          sections: [...state.radarConfig.sections],
          quadrants: [...state.radarConfig.quadrants],
          rings: [...state.radarConfig.rings]
        }
      }
      // Also update localVersion to ensure UI stays in sync
      setLocalVersion(localVersion)

      updateItemAndState(updatedItem)
      setSelectedRelease(updatedRelease)
      setHasUnsavedChanges(false)
    } catch (err) {
      logError('EditItemPage', 'Failed to update release and item:', err)
      setError(
        err instanceof Error ? err.message : 'Failed to update release and item'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAddRelease = async () => {
    if (!item || !selectedRelease || !config || radarId == null) return

    try {
      setIsSubmitting(true)
      const newRelease = {
        releaseDate: new Date().toISOString().split('T')[0],
        title: selectedRelease.title,
        body: selectedRelease.body,
        ring: selectedRelease.ring,
        flag: selectedRelease.flag,
        position: [0, 0] as [number, number],
        id: '' // placeholder, backend should assign real id
      }

      const result = await api.addRelease(item.id, newRelease, Number(radarId))

      const updatedItem = result.items.find(i => i.id === item.id)
      if (updatedItem) {
        updateItemAndState(updatedItem)
        // Get and select the newly added release (it's the last one in the array)
        const newReleaseWithId =
          updatedItem.releases[updatedItem.releases.length - 1]
        setSelectedRelease(newReleaseWithId)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add release')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteRelease = async (releaseId: string) => {
    if (!item || item.releases.length <= 1 || radarId == null) return

    try {
      setIsSubmitting(true)
      const updatedData = await api.deleteRelease(
        item.id,
        releaseId,
        Number(radarId)
      )

      const updatedItem = updatedData.items.find(i => i.id === item.id) || null
      updateItemAndState(updatedItem)

      // Select the most recent remaining release
      if (updatedItem) {
        const sortedReleases = [...updatedItem.releases].sort(
          (a, b) =>
            parseLocalDate(b.releaseDate).getTime() -
            parseLocalDate(a.releaseDate).getTime()
        )
        setSelectedRelease(sortedReleases[0] || null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete release')
    } finally {
      setIsSubmitting(false)
      setIsDeleteModalOpen(false)
    }
  }

  const handleDeleteItem = async () => {
    if (!item || radarId == null) return

    try {
      setIsSubmitting(true)
      const updatedData = await api.deleteItem(item.id, Number(radarId))
      state.radarItems.splice(0, state.radarItems.length, ...updatedData.items)
      // Use navigate directly to go to admin items page
      navigate('/admin/items')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete item')
    } finally {
      setIsSubmitting(false)
      setIsDeleteModalOpen(false)
    }
  }

  const handleDelete = () => {
    if (deleteType === 'item') {
      handleDeleteItem()
    } else if (deleteType === 'release' && selectedRelease) {
      handleDeleteRelease(selectedRelease.id)
      setIsDeleteModalOpen(false)
    }
  }

  // Track unsaved changes
  useEffect(() => {
    if (!selectedRelease || !localRelease) return
    const changed =
      localRelease.title !== selectedRelease.title ||
      localRelease.ring !== selectedRelease.ring ||
      localRelease.flag !== selectedRelease.flag ||
      localRelease.body !== selectedRelease.body ||
      localRelease.releaseDate !== selectedRelease.releaseDate ||
      isArchived !== (item?.archived || false) ||
      (config && localVersion !== config.version)
    setHasUnsavedChanges(Boolean(changed))
  }, [localRelease, selectedRelease, item, isArchived, localVersion, config])

  if (isLoading || !config) {
    return <Loading />
  }

  if (!item) {
    return (
      <ErrorDisplay
        error={
          new Error(
            `Item "${itemId}" not found in ${snap.availableRadars?.find(r => Number(r.id) === Number(radarId))?.name}`
          )
        }
      />
    )
  }

  return (
    <>
      <div className="flex h-full" data-testid="edit-item-page">
        {/* Left Sidebar: Releases Column */}
        <aside
          className="w-[320px] py-6 overflow-auto"
          data-testid="releases-sidebar"
          aria-label="Release history"
        >
          <Card
            className="border border-[var(--ds-border)] bg-[var(--ds-surface)] rounded-[3px] shadow-none p-6"
            data-testid="releases-card"
          >
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <h3
                  className="text-xl font-semibold text-[var(--ds-text)]"
                  id="releases-title"
                >
                  Releases for {selectedRelease?.title ?? ''}
                </h3>
                <p className="text-sm text-[var(--ds-text-subtle)]">
                  Each radar Item can have multiple releases over time.
                </p>
              </div>
              <div className="flex flex-col gap-2">
                {item?.releases.length > 0 &&
                  [...item.releases]
                    .sort(
                      (a, b) =>
                        parseLocalDate(b.releaseDate).getTime() -
                        parseLocalDate(a.releaseDate).getTime()
                    )
                    .map(release => (
                      <button
                        type="button"
                        key={release.id}
                        onClick={() => setSelectedRelease(release)}
                        className={`flex flex-col gap-1 p-3 cursor-pointer w-full text-left text-[var(--ds-text)] rounded border ${
                          release.id === selectedRelease?.id
                            ? 'bg-[var(--ds-background-neutral)] border-[var(--ds-border-focused)]'
                            : 'border-transparent hover:bg-[var(--ds-background-neutral-subtle-hovered)]'
                        }`}
                        data-testid={`release-item-${release.id}`}
                        aria-label={`Release from ${release.releaseDate}: ${release.title}`}
                      >
                        <div className="flex items-center gap-2">
                          {release.id === selectedRelease?.id && (
                            <div className="text-[var(--ds-text-brand)]">→</div>
                          )}
                          <div className="text-sm font-medium text-[var(--ds-text)]">
                            {release.title}
                          </div>
                        </div>
                        <div className="text-xs text-[var(--ds-text-subtle)]">
                          Released: {release.releaseDate}
                        </div>
                      </button>
                    ))}
              </div>
              <div className="mt-auto pt-4 border-t border-[var(--ds-border)]">
                <Button
                  variant="solid"
                  className="bg-primary font-bold text-white rounded-[3px] h-8 px-3 w-full"
                  size="sm"
                  disableRipple
                  onPress={handleAddRelease}
                  isDisabled={isSubmitting}
                  startContent={
                    <PlusIcon className="w-4 h-4" aria-hidden="true" />
                  }
                  data-testid="add-release-button"
                  aria-label="Add new release"
                >
                  Add release
                </Button>
              </div>
            </div>
          </Card>
        </aside>

        {/* Main Content */}
        <div
          className="flex-1 p-6 overflow-auto"
          data-testid="edit-item-content"
        >
          <div className="flex flex-col gap-6 max-w-3xl mx-auto">
            {/* Item Properties Card */}
            <Card
              className="border border-[var(--ds-border)] bg-[var(--ds-surface)] rounded-[3px] shadow-none p-6"
              data-testid="item-properties-card"
            >
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <h3
                    className="text-xl font-semibold text-[var(--ds-text)]"
                    id="properties-title"
                  >
                    Radar Item: {selectedRelease?.title ?? ''}
                  </h3>
                  <div className="h-[1px] bg-[var(--ds-border)]" />
                </div>
                <form
                  className="flex flex-col gap-4"
                  aria-labelledby="properties-title"
                  onSubmit={e => e.preventDefault()}
                >
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="quadrant-select"
                      className="text-[var(--ds-text)] text-sm font-medium"
                      id="quadrant-label"
                    >
                      Quadrant
                    </label>
                    <div className="flex items-center gap-2">
                      <Select
                        aria-label="Quadrant selection"
                        selectedKeys={pendingQuadrant ? [pendingQuadrant] : []}
                        selectionMode="single"
                        onSelectionChange={keys => {
                          const selected = Array.from(keys)[0]?.toString()
                          if (selected) setPendingQuadrant(selected)
                        }}
                        variant="flat"
                        classNames={{
                          base: 'bg-transparent rounded-sm',
                          trigger: `h-10 min-h-0 px-2 py-0.5 text-black dark:text-white bg-transparent border border-[var(--ds-border)] hover:border-blue-500 hover:!bg-transparent data-[hover=true]:!bg-transparent items-center transition-none w-full justify-between rounded-[3px] text-[var(--ds-text)] focus:border-[var(--ds-border-focused)] cursor-pointer`,
                          value: 'truncate',
                          listbox: 'bg-[var(--ds-surface)] shadow-lg',
                          popoverContent: 'bg-[var(--ds-surface)]'
                        }}
                        style={{ color: 'var(--ds-text)', borderWidth: '1px' }}
                        renderValue={selected => {
                          const key = selected?.[0]
                          const option = config.quadrants.find(
                            q => q.id === key
                          )
                          return (
                            <span style={{ color: 'var(--ds-text)' }}>
                              {option?.title ||
                                config.quadrants.find(
                                  q => q.id === pendingQuadrant
                                )?.title ||
                                ''}
                            </span>
                          )
                        }}
                        placeholder={
                          config.quadrants.find(q => q.id === pendingQuadrant)
                            ?.title || 'Select Quadrant'
                        }
                        data-testid="quadrant-dropdown"
                        fullWidth={true}
                        radius="sm"
                        endContent={(() => {
                          const option = config.quadrants.find(
                            q => q.id === pendingQuadrant
                          )
                          return option ? (
                            <span
                              className="w-1 h-4 rounded-xs ml-2"
                              style={{
                                backgroundColor: option.color,
                                display: 'inline-block'
                              }}
                              aria-hidden="true"
                            />
                          ) : null
                        })()}
                      >
                        {config.quadrants.map(quadrant => (
                          <SelectItem
                            key={quadrant.id}
                            className="dropdown-item"
                            classNames={{ selectedIcon: 'flex items-center' }}
                            data-testid={`quadrant-option-${quadrant.id}`}
                          >
                            <div className="flex items-center">
                              <div
                                className="w-1 h-4 rounded-xs mr-2"
                                style={{ backgroundColor: quadrant.color }}
                                aria-hidden="true"
                              />
                              {quadrant.title}
                            </div>
                          </SelectItem>
                        ))}
                      </Select>
                      {pendingQuadrant !== item?.quadrant && (
                        <Button
                          isIconOnly
                          size="sm"
                          isLoading={isSubmitting}
                          onPress={() => {
                            if (item && pendingQuadrant) {
                              handleUpdateItemProperties(
                                pendingQuadrant,
                                pendingTags ?? item.tags,
                                item.archived
                              )
                            }
                          }}
                          aria-label="Save quadrant"
                          className="ml-2 h-8 rounded-[3px] border border-[var(--ds-border)] !bg-blue-700 hover:!bg-blue-600 dark:!bg-blue-600 dark:hover:!bg-blue-500 cursor-pointer focus:outline-none focus:border-[var(--ds-border-focused)] transition"
                          disabled={isSubmitting || !item}
                        >
                          <svg
                            width="20"
                            height="20"
                            fill="none"
                            stroke="white"
                            strokeWidth="2"
                            viewBox="0 0 20 20"
                            aria-label="Save"
                            role="img"
                          >
                            <path
                              d="M5 10l4 4 6-8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="tags-input"
                      className="text-[var(--ds-text)] text-sm font-medium"
                      id="tags-label"
                    >
                      Tags
                    </label>
                    <div className="flex items-center gap-2">
                      <div
                        className="flex flex-wrap gap-2 p-2 min-h-[40px] border bg-[var(--ds-surface)] rounded-[3px] focus-within:border-[var(--ds-border-focused)]"
                        data-testid="tags-container"
                        style={{ flex: 1 }}
                      >
                        {(pendingTags ?? []).map(tag => (
                          <Chip
                            key={tag}
                            variant="flat"
                            onClose={() => {
                              setPendingTags(prev =>
                                (prev ?? item?.tags ?? []).filter(
                                  t => t !== tag
                                )
                              )
                            }}
                            className="bg-[var(--ds-background-neutral)] text-[var(--ds-text)]"
                            size="sm"
                            data-testid={`tag-${tag}`}
                            aria-label={`Remove tag: ${tag}`}
                          >
                            {tag.toUpperCase()}
                          </Chip>
                        ))}
                        <input
                          id="tags-input"
                          type="text"
                          className="flex-1 bg-transparent text-sm text-[var(--ds-text)] outline-none min-w-[120px]"
                          placeholder={
                            (pendingTags ?? []).length
                              ? 'Add more tags...'
                              : 'Type and press Enter or Space to add tags'
                          }
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              const input = e.target as HTMLInputElement
                              const value = input.value.trim().toUpperCase()
                              if (
                                value &&
                                !(pendingTags ?? []).some(tag => tag === value)
                              ) {
                                setPendingTags([...(pendingTags ?? []), value])
                                input.value = ''
                              }
                            } else if (
                              e.key === 'Backspace' &&
                              (e.target as HTMLInputElement).value === ''
                            ) {
                              e.preventDefault()
                              if ((pendingTags ?? []).length > 0) {
                                setPendingTags(prev =>
                                  (prev ?? item?.tags ?? []).slice(0, -1)
                                )
                              }
                            }
                          }}
                          onBlur={e => {
                            const value = e.target.value.trim().toUpperCase()
                            if (
                              value &&
                              !(pendingTags ?? []).some(tag => tag === value)
                            ) {
                              setPendingTags([...(pendingTags ?? []), value])
                              e.target.value = ''
                            }
                          }}
                          data-testid="tags-input"
                          aria-label="Add new tag"
                        />
                      </div>
                      {JSON.stringify(pendingTags ?? []) !==
                        JSON.stringify(item?.tags) && (
                        <Button
                          isIconOnly
                          size="sm"
                          isLoading={isSubmitting}
                          onPress={() => {
                            if (item && pendingTags) {
                              handleUpdateItemProperties(
                                pendingQuadrant ?? item.quadrant,
                                pendingTags,
                                item.archived
                              )
                            }
                          }}
                          aria-label="Save tags"
                          className="ml-2 h-8 rounded-[3px] border border-[var(--ds-border)] !bg-blue-700 hover:!bg-blue-600 dark:!bg-blue-600 dark:hover:!bg-blue-500 cursor-pointer focus:outline-none focus:border-[var(--ds-border-focused)] transition"
                          disabled={isSubmitting || !item}
                        >
                          <svg
                            width="20"
                            height="20"
                            fill="none"
                            stroke="white"
                            strokeWidth="2"
                            viewBox="0 0 20 20"
                            aria-label="Save"
                            role="img"
                          >
                            <path
                              d="M5 10l4 4 6-8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </Button>
                      )}
                    </div>
                  </div>
                </form>
              </div>
            </Card>

            {/* Release Properties Card */}
            <Card
              className="border border-[var(--ds-border)] bg-[var(--ds-surface)] rounded-[3px] shadow-none p-6"
              data-testid="release-properties-card"
            >
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <h3
                    className="text-xl font-semibold text-[var(--ds-text)]"
                    id="release-properties-title"
                  >
                    Selected Release: {localRelease?.releaseDate ?? ''}
                  </h3>
                  <div className="h-[1px] bg-[var(--ds-border)]" />
                </div>
                <form
                  className="flex flex-col gap-4"
                  aria-labelledby="release-properties-title"
                  onSubmit={e => e.preventDefault()}
                >
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="title-input"
                      className="text-[var(--ds-text)] text-sm font-medium"
                      id="title-label"
                    >
                      Title
                    </label>
                    <Input
                      id="title-input"
                      value={localRelease?.title}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleLocalChange({ title: e.target.value })
                      }
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                        }
                      }}
                      classNames={{
                        input:
                          'text-sm group-data-[has-value=true]:text-[var(--ds-text)] outline-none',
                        inputWrapper:
                          'border bg-[var(--ds-surface)] data-[hover=true]:bg-[var(--ds-surface)] focus-within:bg-[var(--ds-surface)] rounded-[3px] data-[focused=true]:border-[var(--ds-border-focused)]'
                      }}
                      data-testid="title-input"
                      aria-labelledby="title-label"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="body-input"
                      className="text-[var(--ds-text)] text-sm font-medium"
                      id="body-label"
                    >
                      Description
                    </label>
                    <RichTextEditor
                      value={localRelease?.body || ''}
                      onChange={(html: string) =>
                        handleLocalChange({ body: html })
                      }
                      minHeight={100}
                      placeholder="Enter a description for this release..."
                      dataTestId="body-input"
                      ariaLabel="Description"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="release-date"
                      className="text-[var(--ds-text)] text-sm font-medium"
                      id="release-date-label"
                    >
                      Release Date
                    </label>
                    <ReleaseDateInput
                      id="release-date"
                      type="date"
                      value={localRelease?.releaseDate}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleLocalChange({ releaseDate: e.target.value })
                      }
                      classNames={{
                        input:
                          'text-sm group-data-[has-value=true]:text-[var(--ds-text)] outline-none',
                        inputWrapper:
                          'border bg-[var(--ds-surface)] data-[hover=true]:bg-[var(--ds-surface)] focus-within:bg-[var(--ds-surface)] rounded-[3px] h-[40px] data-[focused=true]:border-[var(--ds-border-focused)]'
                      }}
                      data-testid="release-date-input"
                      aria-labelledby="release-date-label"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="ring-select"
                      className="text-[var(--ds-text)] text-sm font-medium"
                      id="ring-label"
                    >
                      Ring
                    </label>
                    <Select
                      aria-label="Ring selection"
                      selectedKeys={
                        localRelease?.ring ? [localRelease.ring] : []
                      }
                      selectionMode="single"
                      onSelectionChange={keys => {
                        const key = Array.from(keys)[0]?.toString()
                        if (key) handleLocalChange({ ring: key })
                      }}
                      variant="flat"
                      classNames={{
                        base: 'bg-transparent rounded-sm',
                        trigger: `h-10 min-h-0 px-2 py-0.5 text-black dark:text-white bg-transparent border border-[var(--ds-border)] hover:border-blue-500 hover:!bg-transparent data-[hover=true]:!bg-transparent items-center transition-none w-full justify-between rounded-[3px] text-[var(--ds-text)] focus:border-[var(--ds-border-focused)] cursor-pointer`,
                        value: 'truncate',
                        listbox: 'bg-[var(--ds-surface)] shadow-lg',
                        popoverContent: 'bg-[var(--ds-surface)]'
                      }}
                      style={{ color: 'var(--ds-text)', borderWidth: '1px' }}
                      renderValue={selected => {
                        const key = selected?.[0]
                        const option = config.rings.find(r => r.id === key)
                        return (
                          <span style={{ color: 'var(--ds-text)' }}>
                            {option?.title ||
                              config.rings.find(
                                r => r.id === localRelease?.ring
                              )?.title ||
                              ''}
                          </span>
                        )
                      }}
                      placeholder={
                        config.rings.find(r => r.id === localRelease?.ring)
                          ?.title || 'Select Ring'
                      }
                      data-testid="ring-dropdown"
                      fullWidth={true}
                      radius="sm"
                      endContent={(() => {
                        const option = config.rings.find(
                          r => r.id === localRelease?.ring
                        )
                        return option ? (
                          <span
                            className="w-1 h-4 rounded-xs ml-2"
                            style={{
                              backgroundColor: option.color,
                              display: 'inline-block'
                            }}
                            aria-hidden="true"
                          />
                        ) : null
                      })()}
                    >
                      {config.rings.map(ring => (
                        <SelectItem
                          key={ring.id}
                          className="dropdown-item"
                          classNames={{ selectedIcon: 'flex items-center' }}
                          data-testid={`ring-option-${ring.id}`}
                        >
                          <div className="flex items-center">
                            <div
                              className="w-1 h-4 rounded-xs mr-2"
                              style={{ backgroundColor: ring.color }}
                              aria-hidden="true"
                            />
                            {ring.title}
                          </div>
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="flag-select"
                      className="text-[var(--ds-text)] text-sm font-medium"
                      id="flag-label"
                    >
                      Status
                    </label>
                    <Select
                      aria-label="Status selection"
                      selectedKeys={
                        localRelease?.flag ? [localRelease.flag] : []
                      }
                      selectionMode="single"
                      onSelectionChange={keys => {
                        const key = Array.from(keys)[0]?.toString()
                        if (key) handleLocalChange({ flag: key as Flag })
                      }}
                      variant="flat"
                      classNames={{
                        base: 'bg-transparent rounded-sm',
                        trigger: `h-10 min-h-0 px-2 py-0.5 text-black dark:text-white bg-transparent border border-[var(--ds-border)] hover:border-blue-500 hover:!bg-transparent data-[hover=true]:!bg-transparent items-center transition-none w-full justify-between rounded-[3px] text-[var(--ds-text)] focus:border-[var(--ds-border-focused)] cursor-pointer`,
                        value: 'truncate',
                        listbox: 'bg-[var(--ds-surface)] shadow-lg',
                        popoverContent: 'bg-[var(--ds-surface)]'
                      }}
                      style={{ color: 'var(--ds-text)', borderWidth: '1px' }}
                      renderValue={selected => {
                        const key = selected?.[0]
                        const flag =
                          config.flags[key as keyof typeof config.flags]
                        return (
                          <span style={{ color: 'var(--ds-text)' }}>
                            {flag?.title ||
                              config.flags[
                                (localRelease?.flag ||
                                  'default') as keyof typeof config.flags
                              ]?.title ||
                              ''}
                          </span>
                        )
                      }}
                      placeholder={
                        config.flags[
                          (localRelease?.flag ||
                            'default') as keyof typeof config.flags
                        ]?.title || 'Select Status'
                      }
                      data-testid="flag-dropdown"
                      fullWidth={true}
                      radius="sm"
                      endContent={(() => {
                        const flag =
                          config.flags[
                            (localRelease?.flag ||
                              'default') as keyof typeof config.flags
                          ]
                        return flag ? (
                          <span
                            className="w-1 h-4 rounded-xs ml-2"
                            style={{
                              backgroundColor: flag.color,
                              display: 'inline-block'
                            }}
                            aria-hidden="true"
                          />
                        ) : null
                      })()}
                    >
                      {Object.entries(config.flags).map(([key, flag]) => (
                        <SelectItem
                          key={key}
                          className="dropdown-item"
                          classNames={{ selectedIcon: 'flex items-center' }}
                          data-testid={`flag-option-${key}`}
                        >
                          <div className="flex items-center">
                            <div
                              className="w-1 h-4 rounded-xs mr-2"
                              style={{ backgroundColor: flag.color }}
                              aria-hidden="true"
                            />
                            <span className="capitalize">{flag.title}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
                  <div className="flex items-center justify-between gap-4 mt-4 pt-4 border-t border-[var(--ds-border)]">
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor="version-input"
                        className="text-[var(--ds-text)] text-sm font-medium"
                        id="version-label"
                      >
                        Radar Version:
                      </label>
                      <Input
                        id="version-input"
                        value={localVersion}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleVersionChange(e.target.value)
                        }
                        size="sm"
                        placeholder="1.0"
                        classNames={{
                          base: 'max-w-[120px]',
                          input:
                            'text-xs group-data-[has-value=true]:text-[var(--ds-text)] outline-none',
                          inputWrapper:
                            'border bg-[var(--ds-surface)] data-[hover=true]:bg-[var(--ds-surface)] focus-within:bg-[var(--ds-surface)] rounded-[3px] data-[focused=true]:border-[var(--ds-border-focused)]'
                        }}
                        data-testid="version-input"
                        aria-labelledby="version-label"
                      />
                    </div>
                    <Button
                      variant="solid"
                      className="bg-primary text-white hover:bg-hover font-bold rounded-[3px] h-8 px-3 cursor-pointer"
                      size="sm"
                      disableRipple
                      isDisabled={!hasUnsavedChanges || isSubmitting}
                      onPress={handleUpdateReleaseAndItem}
                      data-testid="save-changes-button"
                      aria-label="Save all changes"
                    >
                      Update Release
                    </Button>
                  </div>
                </form>
              </div>
            </Card>

            {/* Danger Zone Card */}
            <Card
              className="border border-[var(--ds-border)] bg-[var(--ds-surface)] rounded-[3px] shadow-none p-6"
              data-testid="danger-zone-card"
            >
              <div className="flex flex-col gap-4">
                <h3 className="text-lg font-semibold text-[var(--ds-text)]">
                  Item Management
                </h3>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between p-4 border border-[var(--ds-border)] rounded-[3px] bg-[var(--ds-background-neutral-subtle)]">
                    <div className="flex flex-col gap-1">
                      <h4 className="text-sm font-medium text-[var(--ds-text)]">
                        Archive Item
                      </h4>
                      <p className="text-xs text-[var(--ds-text-subtle)]">
                        Hide this item from the radar while preserving visbility
                        in search and quadrant views. You can unarchive it at
                        any time.
                      </p>
                    </div>
                    <Switch
                      isSelected={isArchived}
                      onValueChange={handleArchiveToggle}
                      data-testid="archive-toggle"
                      aria-label="Archive item"
                      className="ml-4"
                      isDisabled={isSubmitting}
                    />
                  </div>
                  <div className="h-[1px] bg-[var(--ds-border)]" />
                  <div>
                    <h3 className="text-lg font-semibold text-[#DE350B] mb-4">
                      ⚠️ Danger Zone
                    </h3>
                    <div className="flex flex-col gap-4">
                      {item.releases.length > 1 && localRelease && (
                        <div className="flex items-center justify-between p-4 border border-[var(--ds-border-danger)] bg-[var(--ds-background-danger-subtle)] rounded-[3px]">
                          <div className="flex flex-col gap-1">
                            <h4 className="text-sm font-medium text-[var(--ds-text)]">
                              Delete Current Release
                            </h4>
                            <p className="text-xs text-[var(--ds-text-subtle)]">
                              Remove this specific release (
                              {localRelease?.releaseDate ?? ''}) while keeping
                              the item and other releases.
                            </p>
                          </div>
                          <Button
                            variant="light"
                            color="danger"
                            className="rounded-[3px] h-8 px-3"
                            size="sm"
                            disableRipple
                            onPress={() => {
                              setDeleteType('release')
                              setIsDeleteModalOpen(true)
                            }}
                            data-testid="delete-release-button"
                            aria-label={`Delete release from ${localRelease?.releaseDate ?? ''}`}
                          >
                            Delete Release
                          </Button>
                        </div>
                      )}
                      <div className="flex items-center justify-between p-4 border border-[var(--ds-border-danger)] bg-[var(--ds-background-danger-subtle)] rounded-[3px]">
                        <div className="flex flex-col gap-1">
                          <h4 className="text-sm font-medium text-[var(--ds-text)]">
                            Delete Radar Item
                          </h4>
                          <p className="text-xs text-[var(--ds-text-subtle)]">
                            Permanently delete this radar item and all its
                            releases. This action cannot be undone.
                          </p>
                        </div>
                        <Button
                          variant="light"
                          color="danger"
                          className="rounded-[3px] h-8 px-3"
                          size="sm"
                          disableRipple
                          onPress={() => {
                            setDeleteType('item')
                            setIsDeleteModalOpen(true)
                          }}
                          data-testid="delete-item-button"
                          aria-label={`Delete item: ${selectedRelease?.title ?? ''}`}
                        >
                          Delete Item
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        data-testid="delete-modal"
        aria-labelledby="delete-modal-title"
        classNames={{
          base: 'heroui-modal-content',
          header: 'heroui-modal-header',
          body: 'heroui-modal-body',
          footer: 'heroui-modal-footer'
        }}
      >
        <ModalContent>
          <ModalHeader>
            <h3
              id="delete-modal-title"
              className="text-lg font-medium text-[var(--ds-text)]"
            >
              Delete{' '}
              {deleteType === 'item'
                ? (selectedRelease?.title ?? '')
                : `Release (${localRelease?.releaseDate ?? ''})`}
            </h3>
          </ModalHeader>
          <ModalBody>
            <p>
              Are you sure you want to delete{' '}
              {deleteType === 'item'
                ? `"${selectedRelease?.title ?? ''}"`
                : `the release from ${localRelease?.releaseDate ?? ''}`}
              ? This action cannot be undone.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="bordered"
              className="text-[var(--ds-text)] rounded-[3px] h-8 px-3 border border-[var(--ds-border)] bg-transparent hover:bg-transparent"
              size="sm"
              disableRipple
              onPress={() => setIsDeleteModalOpen(false)}
              data-testid="cancel-delete-button"
              aria-label="Cancel deletion"
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="danger"
              className="rounded-[3px] h-8 px-3"
              size="sm"
              disableRipple
              onPress={handleDelete}
              data-testid="confirm-delete-button"
              aria-label={`Confirm deletion of ${deleteType === 'item' ? (selectedRelease?.title ?? '') : `release from ${localRelease?.releaseDate ?? ''}`}`}
            >
              Delete{' '}
              {deleteType === 'item'
                ? (selectedRelease?.title ?? '')
                : `Release (${localRelease?.releaseDate ?? ''})`}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
export default AdminEditItemPage
